import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, SubTopBar, TasksStore, Title } from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { AppStore } from '../Core/AppStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { ManageDisplayListCard } from '../Displays/ManageDisplayListCard';
import { DisplayGroupsStore } from './DisplayGroupsStore';

export interface IManageDisplayGroupParams {
    name: string;
}

export interface IManageGroupProps extends RouteComponentProps<IManageDisplayGroupParams> {}

@autoinject
@observer
export class ManageDisplayGroup extends React.Component<IManageGroupProps> {
    public constructor(props: IManageGroupProps) {
        super(props);

        this.gvm = new DisplayListViewModel({
            displayFilter: {
                displayGroups: [this.props.match.params.name],
            },
            columns: [
                { option: 'Serial Number' },
                { option: 'Unit Serial Number' },
                { option: 'Site ID' },
                { option: 'IP Address' },
                { option: 'Mri Software' },
            ],
            nocache: true,
        });
        this.vm = new DisplayListViewModel({
            exceptDisplayFilter: {
                displayGroups: [this.props.match.params.name],
            },
            columns: [
                { option: 'Serial Number' },
                { option: 'Unit Serial Number' },
                { option: 'Site ID' },
                { option: 'IP Address' },
                { option: 'Mri Software' },
            ],
            nocache: true,
        });
    }

    public async componentDidMount() {
        try {
            const p1 = await this.displayGroupsStore.loadAsync(this.props.match.params.name);
            const p2 = await this.gvm.loadMoreAsync();
            const p3 = await this.vm.loadMoreAsync();
            const p4 = await this.vm.loadContractsAsync();

            await Promise.all([p1, p2, p3, p4]);
        } catch (e: any) {
            this.appStore.setError(e);
        }
    }

    public componentWillUnmount() {
        if (this.addTimer) window.clearTimeout(this.addTimer);
        if (this.removeTimer) window.clearTimeout(this.removeTimer);
    }

    public render() {
        const location = `/display-groups`;
        return (
            <div className="container-fluid pt-3">
                <Title>Manage {this.props.match.params.name}</Title>
                <SubTopBar>
                    <LinkContainer exact to={location}>
                        <a className="btn btn-link">
                            <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Groups
                        </a>
                    </LinkContainer>
                </SubTopBar>

                <div className="row">
                    <div className="col-12 col-md-6 mb-3">
                        <ManageDisplayListCard
                            vm={this.gvm}
                            title="Group Displays"
                            actionMessage="Removing..."
                            actionTitle="Remove"
                            runningAction={this.removing}
                            action={this.removeSelected}
                            height={600}
                        />
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <ManageDisplayListCard
                            vm={this.vm}
                            title="All Displays"
                            actionMessage="Adding..."
                            actionTitle="Add"
                            runningAction={this.adding}
                            action={this.addSelected}
                            renderDisplayFilter
                            height={600}
                        />
                    </div>
                </div>
            </div>
        );
    }

    @bind
    private async refreshAsync() {
        const p1 = this.gvm.reloadAsync();
        const p2 = this.vm.reloadAsync();

        await Promise.all([p1, p2]);
    }

    @bind
    @action
    private async removeSelected(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.removing = true;
        const task = this.tasksStore.startSessionTask(`Remove Displays`);

        try {
            await this.displayGroupsStore.removeDisplayAsync(this.props.match.params.name, {
                displaySerialNumbers: this.gvm.checkedDisplays.map(d => d.serialNumber),
            });
            this.removeTimer = window.setTimeout(this.refreshAsync, 1000);
            task.complete();
        } catch (e: any) {
            task.error(e);
        } finally {
            this.removing = false;
        }

        this.gvm.reloadAsync();
    }

    @bind
    @action
    private async addSelected(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.adding = true;
        const task = this.tasksStore.startSessionTask(`Add Displays`);

        try {
            await this.displayGroupsStore.addDisplayAsync(this.props.match.params.name, {
                displaySerialNumbers: this.vm.checkedDisplays.map(d => d.serialNumber),
            });
            this.addTimer = window.setTimeout(this.refreshAsync, 1000);
            task.complete();
        } catch (e: any) {
            task.error(e);
        } finally {
            this.adding = false;
        }

        this.gvm.reloadAsync();
    }

    @observable
    private removing = false;
    @observable
    private adding = false;
    private addTimer?: number;
    private removeTimer?: number;
    private gvm: DisplayListViewModel;
    private vm: DisplayListViewModel;
    @inject
    private appStore!: AppStore;
    @inject
    private displayGroupsStore!: DisplayGroupsStore;
    @inject
    private tasksStore!: TasksStore;
}
